import { Trans } from 'react-i18next';
import { View } from 'react-native';
import React, { useState } from 'react';

import { Button } from '@components/Button';
import { CircledIcon } from '@components/CircledIcon';
import { Icon } from '@components/Icon';
import { Map } from '@components/Map';
import { Modal } from '@components/Modal';
import { Order } from '@lib/types';
import { Typography } from '@components/Typography';
import { makeStyles } from '@providers/Theme';
import { useIsMobile } from '@hooks';

import { UserDetailsRow } from './UserDetailsRow';

type UserMapProps = {
  order: Order;
};

const useStyles = makeStyles((theme) => ({
  root: {
    flexDirection: 'row',
    marginTop: theme.spacing(2),
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  icon: {
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
}));

export const UserMap: React.FC<UserMapProps> = ({ order }) => {
  const styles = useStyles();
  const [userMapOpen, setUserMapOpen] = useState(false);
  const isMobile = useIsMobile();

  const isFleet = order?.orderMessage?.data?.fleet;

  if (!isFleet && !order?.orderMessage?.data?.deliveryAddress?.location) {
    return null;
  }

  return (
    <View style={styles.root}>
      {isFleet ? (
        <UserDetailsRow
          icon="trips"
          value={
            <Typography weight="bold">
              <Trans
                i18nKey="userMap.elmenusWillDeliverThisOrder"
                defaults="<0>elmenus</0>&nbsp;will deliver this order for you."
                // @ts-ignore
                components={[<Typography variant="error" />]}
              />
            </Typography>
          }
        />
      ) : (
        <Button
          variant="secondary"
          startIcon={
            <Icon
              name="map"
              color={(theme) => theme.palette.base[900]}
              viewBoxWidth={25}
            />
          }
          onPress={() => setUserMapOpen(true)}
          fullWidth={isMobile}>
          <Trans
            i18nKey="userMap.buttonText"
            defaults="View Customer Location"
          />
        </Button>
      )}
      {order?.orderMessage?.data?.deliveryAddress?.location && (
        <Modal
          open={userMapOpen}
          onRequestClose={() => setUserMapOpen(false)}
          dismissOnBackdropPress
          animationType="fade">
          <View style={styles.modalContent}>
            <CircledIcon icon="pin" preset="error" style={styles.icon} />
            <Typography size="2xl" weight="semiBold" style={styles.title}>
              <Trans i18nKey="userMap.title" defaults="Customer Location" />
            </Typography>
            <Map location={order?.orderMessage?.data?.deliveryAddress?.location} />
          </View>
        </Modal>
      )}
    </View>
  );
};
